/* eslint-disable @typescript-eslint/naming-convention */
import debounce from 'lodash.debounce';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import axios, { CancelTokenSource } from 'axios';
import { FieldState, FormState } from 'formstate';
import { inject, injectable } from '@servicetitan/react-ioc';
import { toast } from '@servicetitan/anvil2';

import {
    EquipmentManufacturer,
    EquipmentModelNumber,
    EquipmentModelSeries,
    toManufacturerApiModel,
    toManufacturerViewModel,
    toModelNumberViewModel,
    toModelSeriesApiModel,
    toModelSeriesViewModel,
} from '../models/equipment.models';
import { EquipmentsApi } from '../../common/api/equipments.api';
import { MainStore } from '../../common/stores/main.store';

import { getBaseRequestParams } from '../../common/utils/base-request-params';

export interface ManualEquipmentData {
    manufacturer?: EquipmentManufacturer;
    modelSeries?: EquipmentModelSeries;
    modelNumber?: EquipmentModelNumber;
}

const wait = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

@injectable()
export class EquipmentManualSelectStore {
    @observable isSearching = false;

    @observable manufacturesIsLoading = false;
    @observable manufacturesSearchQuery = '';
    @observable manufacturers?: EquipmentManufacturer[] = [];

    @observable modelSeriesSearchQuery = '';
    @observable modelSeries?: EquipmentModelSeries[];

    @observable modelNumberSearchQuery = '';
    @observable modelNumbers?: EquipmentModelNumber[];

    locationId!: number;

    formState = new FormState({
        manufacturerFieldState: new FieldState<EquipmentManufacturer | undefined>(undefined),
        modelSeriesFieldState: new FieldState<EquipmentModelSeries | undefined>(undefined),
        modelNumberFieldState: new FieldState<EquipmentModelNumber | undefined>(undefined),
    })
        .compose()
        .validators(formState => {
            const allSet = formState.modelNumberFieldState.value;
            return allSet ? undefined : 'Please fill all fields';
        });

    private loadManufacturerCancelTokenSource: CancelTokenSource | undefined = undefined;
    private loadModelSeriesCancelTokenSource: CancelTokenSource | undefined = undefined;
    private loadModelNumbersCancelTokenSource: CancelTokenSource | undefined = undefined;

    constructor(
        @inject(EquipmentsApi) private readonly equipmentsApi: EquipmentsApi,
        @inject(MainStore) private readonly mainStore: MainStore
    ) {
        makeObservable(this);

        this.locationId = this.mainStore.selectedAppointment!.locationId;

        this.formState.$.manufacturerFieldState.onDidChange(this.onManufacturerDidChange);
        this.formState.$.modelSeriesFieldState.onDidChange(this.onModelSeriesDidChange);
        this.formState.$.modelNumberFieldState.onDidChange(this.onModelNumberDidChange);

        this.formState.enableAutoValidationAndValidate();

        reaction(
            () => this.manufacturesSearchQuery,
            debounce(() => {
                this.loadManufacturers(this.manufacturesSearchQuery);
            }, 300)
        );

        reaction(
            () => this.modelSeriesSearchQuery,
            debounce(() => {
                this.loadModelSeries(this.modelSeriesSearchQuery);
            }, 300)
        );

        reaction(
            () => this.modelNumberSearchQuery,
            debounce(() => {
                if (
                    this.modelNumberSearchQuery.length >= 4 ||
                    this.formState.$.manufacturerFieldState.value !== undefined ||
                    this.formState.$.modelSeriesFieldState.value !== undefined
                ) {
                    this.loadModelNumbers(this.modelNumberSearchQuery);
                }
            }, 300)
        );
        // TODO: remove in prod
        // this.dddd();
    }

    dddd = async () => {
        this.formState.$.manufacturerFieldState.onChange({
            text: 'Lennox',
            label: 'Lennox',
            id: 'ce55ac13-7b7c-4f84-b2dd-c7fc3b7e1447',
        });

        await wait(200);

        this.formState.$.modelNumberFieldState.onChange({
            text: 'GSR21Q5-100-3',
            label: 'GSR21Q5-100-3',
            id: 'b402df76-10d0-4826-908f-fb548e493fee',
            modelSeries: {
                text: 'GSR21Q',
                label: 'GSR21Q',
                id: '6fd7b41d-9c9b-4040-8f1d-c1f2570f250b',
                manufacturer: {
                    text: 'Lennox',
                    label: 'Lennox',
                    id: 'ce55ac13-7b7c-4f84-b2dd-c7fc3b7e1447',
                },
            },
            hasDocumentation: true,
            notes: 'Multiple Part Options',
            brandeImage:
                'https://assets-bluon-prod.sfo3.cdn.digitaloceanspaces.com/brands/logo/O97tCi1LQU6Yz9IAeh2N_lennox.png',
            modelImage:
                'https://assets-bluon-prod.sfo3.cdn.digitaloceanspaces.com/oems/unit_image/LN_GSR21Q.png',
        });

        await wait(100);
    };

    // -------------Manufacturers---------------

    @action
    onManufacturerDidChange = ({ newValue }: { newValue: EquipmentManufacturer | undefined }) => {
        this.modelSeries = [];
        this.modelSeriesSearchQuery = '';
        this.formState.$.modelSeriesFieldState.onChange(undefined);

        this.modelNumbers = [];
        this.modelNumberSearchQuery = '';
        this.formState.$.modelNumberFieldState.onChange(undefined);

        if (newValue) {
            this.loadModelSeries();
            this.loadModelNumbers();
        }
    };

    onModelSeriesDidChange = ({ newValue }: { newValue: EquipmentModelSeries | undefined }) => {
        this.modelNumbers = [];
        this.modelNumberSearchQuery = '';
        this.formState.$.modelNumberFieldState.onChange(undefined);

        if (this.formState.$.manufacturerFieldState.value === undefined) {
            this.formState.$.manufacturerFieldState.onDidChange(() => {});
            this.formState.$.manufacturerFieldState.onChange(newValue?.manufacturer);
            this.formState.$.manufacturerFieldState.onDidChange(this.onManufacturerDidChange);
        }

        if (newValue) {
            this.loadModelNumbers();
        }
    };

    onModelNumberDidChange = ({ newValue }: { newValue: EquipmentModelNumber | undefined }) => {
        if (this.formState.$.manufacturerFieldState.value === undefined) {
            this.formState.$.manufacturerFieldState.onDidChange(() => {});
            this.formState.$.manufacturerFieldState.onChange(newValue?.modelSeries.manufacturer);
            this.formState.$.manufacturerFieldState.onDidChange(this.onManufacturerDidChange);
        }

        if (this.formState.$.modelSeriesFieldState.value === undefined) {
            this.formState.$.modelSeriesFieldState.onDidChange(() => {});
            this.formState.$.modelSeriesFieldState.onChange(newValue?.modelSeries);
            this.formState.$.modelSeriesFieldState.onDidChange(this.onModelSeriesDidChange);
        }
    };

    @action
    loadManufacturers = async (searchQuery?: string) => {
        this.loadManufacturerCancelTokenSource?.cancel('new search performed');
        this.loadManufacturerCancelTokenSource = axios.CancelToken.source();
        this.isSearching = true;

        try {
            const response = await this.equipmentsApi.getEquipmentManufacturers(
                {
                    ...getBaseRequestParams(),
                    search_text: searchQuery,
                    number_of_response_items: 20,
                    location_id: this.locationId,
                },
                this.loadManufacturerCancelTokenSource.token
            );
            runInAction(() => {
                this.manufacturers =
                    response.data.manufacturer_candidates.map(toManufacturerViewModel);
                this.isSearching = false;
            });
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return;
            }
            runInAction(() => {
                this.isSearching = false;
            });
            throw e;
        }
    };

    @action
    setManufacturersSearchQuery = (query: string) => {
        this.manufacturesSearchQuery = query;
    };

    @action
    onManufacturerChange = (manufacturer?: EquipmentManufacturer) => {
        this.formState.$.manufacturerFieldState.onChange(manufacturer);
    };

    @computed
    get manufacturersOptions() {
        return this.manufacturers;
    }

    // -------------Model Series---------------

    @action
    loadModelSeries = async (searchQuery?: string) => {
        this.loadModelSeriesCancelTokenSource?.cancel('new search performed');
        this.loadModelSeriesCancelTokenSource = axios.CancelToken.source();
        this.isSearching = true;
        try {
            const response = await this.equipmentsApi.getEquipmentModelSeries(
                {
                    ...getBaseRequestParams(),
                    manufacturer_selection: toManufacturerApiModel(
                        this.formState.$.manufacturerFieldState.value
                    ),
                    search_text: searchQuery,
                    number_of_response_items: 20,
                    location_id: this.locationId,
                },
                this.loadModelSeriesCancelTokenSource.token
            );
            runInAction(() => {
                this.isSearching = false;
                this.modelSeries =
                    response.data.model_series_candidates.map(toModelSeriesViewModel);
            });
        } catch (e) {
            if (axios.isCancel(e)) {
                return;
            }
            runInAction(() => {
                this.isSearching = false;
            });
            throw e;
        }
    };

    @action
    setModelSeriesSearchQuery = (query: string) => {
        this.modelSeriesSearchQuery = query;
    };

    @action
    onModelSeriesChange = (modelSeries?: EquipmentModelSeries) => {
        this.formState.$.modelSeriesFieldState.onChange(modelSeries);
    };

    @computed
    get modelSeriesOptions() {
        return this.modelSeries;
    }

    // -------------Model Numbers---------------

    @action
    loadModelNumbers = async (searchQuery?: string) => {
        this.loadModelNumbersCancelTokenSource?.cancel('new search performed');
        this.loadModelNumbersCancelTokenSource = axios.CancelToken.source();
        this.isSearching = true;
        try {
            const response = await this.equipmentsApi.getEquipmentModelNumbers(
                {
                    ...getBaseRequestParams(),
                    manufacturer_selection: toManufacturerApiModel(
                        this.formState.$.manufacturerFieldState.value
                    ),
                    model_series_selection: toModelSeriesApiModel(
                        this.formState.$.modelSeriesFieldState.value
                    ),

                    search_text:
                        searchQuery && searchQuery.trim() === ''
                            ? undefined
                            : searchQuery?.slice(0, 512),
                    number_of_response_items: 20,
                    location_id: this.locationId,
                },
                this.loadModelNumbersCancelTokenSource.token
            );
            runInAction(() => {
                this.isSearching = false;
                this.modelNumbers = response.data.model_number_candidates
                    .filter(x => !!x.model_series)
                    .map(toModelNumberViewModel);
            });
        } catch (e) {
            runInAction(() => {
                this.isSearching = false;
            });

            if (axios.isCancel(e)) {
                return;
            }
            if (axios.isAxiosError(e)) {
                toast.danger({ title: 'Something went wrong.', duration: 5000 });
                return;
            }
            throw e;
        }
    };

    @action
    setModelNumberSearchQuery = (query: string) => {
        this.modelNumberSearchQuery = query;
    };

    @action
    onModelNumberChange = (modelNumber?: EquipmentModelNumber) => {
        this.formState.$.modelNumberFieldState.onChange(modelNumber);
    };

    @computed
    get modelNumberOptions() {
        return this.modelNumbers;
    }

    @computed
    get modelNumberSearchInfoText() {
        if (
            this.formState.$.manufacturerFieldState.value === undefined &&
            this.formState.$.modelSeriesFieldState.value === undefined
        ) {
            return 'Type at least 4 characters to view matches';
        }
    }

    // -------------Form State---------------

    getFormData = async (): Promise<ManualEquipmentData | undefined> => {
        const validationResult = await this.formState.validate();
        if (validationResult.hasError && this.formState.formError) {
            return undefined;
        }
        return {
            manufacturer: this.formState.$.manufacturerFieldState.$,
            modelSeries: this.formState.$.modelSeriesFieldState.$,
            modelNumber: this.formState.$.modelNumberFieldState.$,
        };
    };
}
